import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Message envoyé avec succès ! Nous vous répondrons sous 24h.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        toast.error(data.detail || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Impossible d\'envoyer le message. Vérifiez votre connexion internet.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'contact@enovationweb.com',
      link: 'mailto:contact@enovationweb.com'
    },
    {
      icon: Phone,
      title: 'Téléphone',
      content: '+216 22 360 360',
      link: 'tel:+21622360360'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'Rue du Lac Victoria, Les Berges du Lac 1, 1053 Tunis, Tunisie',
      link: 'https://maps.google.com'
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun - Ven : 9h00 - 18h00',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Contactez-Nous
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Vous avez un projet digital ? Parlons-en ! Notre équipe est là pour transformer 
              vos idées en réalité.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactInfo.map((info, index) => {
                const IconComponent = info.icon;
                return (
                  <Card 
                    key={index}
                    className="p-6 hover:shadow-lg transition-all duration-300 border-2 border-slate-100 rounded-xl"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                        {info.link ? (
                          <a 
                            href={info.link}
                            className="text-slate-600 hover:text-emerald-600 transition-colors duration-200"
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-slate-600">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* Additional Info */}
              <Card className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl">
                <h3 className="font-semibold text-lg mb-3">Besoin d'une réponse rapide ?</h3>
                <p className="text-emerald-100 text-sm leading-relaxed mb-4">
                  Nous répondons à toutes les demandes sous 24h. Pour les urgences, 
                  n'hésitez pas à nous appeler directement.
                </p>
                <a href="tel:+21622360360">
                  <Button className="w-full bg-white text-emerald-600 hover:bg-emerald-50 rounded-full font-semibold transition-all duration-200">
                    Appeler Maintenant
                  </Button>
                </a>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 lg:p-10 shadow-xl border-2 border-slate-100 rounded-2xl">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Envoyez-nous un Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-medium">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jean Dupont"
                        className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jean.dupont@example.com"
                        className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+33 6 12 34 56 78"
                        className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-slate-700 font-medium">
                        Entreprise
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Nom de votre entreprise"
                        className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service" className="text-slate-700 font-medium">
                      Service Concerné *
                    </Label>
                    <Select value={formData.service} onValueChange={handleServiceChange} required>
                      <SelectTrigger className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Sélectionnez un service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="creation-web">Création de Sites Web</SelectItem>
                        <SelectItem value="ia">Intelligence Artificielle</SelectItem>
                        <SelectItem value="seo">Référencement SEO</SelectItem>
                        <SelectItem value="sea">Publicité SEA</SelectItem>
                        <SelectItem value="social-media">Gestion Réseaux Sociaux</SelectItem>
                        <SelectItem value="autre">Autre / Conseil Global</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Votre Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Décrivez-nous votre projet et vos objectifs..."
                      rows={6}
                      className="rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer le Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-slate-500 text-center">
                    * Champs obligatoires. Vos données sont protégées et ne seront jamais partagées.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="h-96 bg-slate-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center bg-white p-8 rounded-xl shadow-lg">
            <MapPin className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <p className="text-slate-700 font-medium">Rue du Lac Victoria</p>
            <p className="text-slate-600">Les Berges du Lac 1</p>
            <p className="text-slate-600">1053 Tunis, Tunisie</p>
          </div>
        </div>
      </section>
    </div>
  );
};
